import { useGetDataLayer } from '@/services/adapters/useGetDataLayer'
import { useGetDataLayers } from '@/services/adapters/useGetDataLayers'
import { useGetLayerPacks } from '@/services/adapters/useGetLayerPacks'
import {
  IBaseDataLayer,
  IBaseDataLayerReference,
  IDataLayer,
  IDataLayerReference
} from '@lifk/types'
import { computed, ComputedRef, inject, InjectionKey, provide } from 'vue'

type TFullDataLayersStore = {
  fullDataLayers: ComputedRef<IDataLayer[]>
  dataLayerReference: ComputedRef<IDataLayerReference | undefined>
  dataLayersByPack: ComputedRef<Record<string, IDataLayer[]>>
}
export const FullDataLayersKey = Symbol() as InjectionKey<TFullDataLayersStore>

export const provideFullDataLayers = () => {
  const { data: dataLayer } = useGetDataLayer()
  const { data: dataLayers } = useGetDataLayers()
  const { data: layerPacks } = useGetLayerPacks()

  const getDataLayerWithAssociations = (
    dataLayer: IBaseDataLayer | IBaseDataLayerReference
  ): IDataLayer | IDataLayerReference => {
    const associatedLPs = layerPacks.value?.filter((lp) =>
      lp.dataLayers.includes(dataLayer.key)
    )
    return {
      ...dataLayer,
      layerPacks: associatedLPs ?? []
    }
  }

  const fullDataLayers: ComputedRef<IDataLayer[]> = computed(() =>
    (dataLayers.value ?? []).map(
      (dataLayer) => getDataLayerWithAssociations(dataLayer) as IDataLayer
    )
  )

  const dataLayerReference: ComputedRef<IDataLayerReference | undefined> =
    computed(() => {
      if (!dataLayer.value) return
      return getDataLayerWithAssociations(
        dataLayer.value
      ) as IDataLayerReference
    })

  // For quick data layer lookup by Layer Pack _id
  const dataLayersByPack: ComputedRef<Record<string, IDataLayer[]>> = computed(
    () =>
      (layerPacks.value ?? []).reduce(
        (acc: Record<string, IDataLayer[]>, layerPack) => {
          // Keep layer pack data layer ordering in tact
          acc[layerPack._id] = layerPack.dataLayers
            .map((layerKey) =>
              fullDataLayers.value.find(({ key }) => layerKey === key)
            )
            .filter((dl) => !!dl)
          return acc
        },
        {}
      )
  )

  provide(FullDataLayersKey, {
    fullDataLayers,
    dataLayerReference,
    dataLayersByPack
  })
}

export default function useFullDataLayers(): TFullDataLayersStore {
  return inject(FullDataLayersKey)!
}
